"use client";

import type { FormBlock } from "@mw/shared/payload-types";

import clsx from "clsx";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";

import { submitCulturePartnership, submitTalentRepresentation, submitVCApplication } from "@/app/_api/hubspot";
import { FormInlineWrapper } from "@/app/_components/HubspotForms/shared/FormInlineWrapper";
import {
  InlineSuccessScreen,
  SuccessStep,
  SuccessStepList,
} from "@/app/_components/HubspotForms/shared/InlineSuccessScreen";

const VCFormDialogButton = dynamic(() => import("@/app/_components/HubspotForms/VCForm/VCFormDialogButton"));
const VCFormLazy = dynamic(() =>
  import("@/app/_components/HubspotForms/VCForm/VCForm").then(mod => ({ default: mod.VCForm })),
);
const TalentRepresentationFormDialogButton = dynamic(
  () => import("@/app/_components/HubspotForms/TalentRepresentationForm/TalentRepresentationFormDialogButton"),
);
const TalentRepresentationFormLazy = dynamic(() =>
  import("@/app/_components/HubspotForms/TalentRepresentationForm/TalentRepresentationForm").then(mod => ({
    default: mod.TalentRepresentationForm,
  })),
);
const CulturePartnershipFormDialogButton = dynamic(
  () => import("@/app/_components/HubspotForms/CulturePartnershipForm/CulturePartnershipFormDialogButton"),
);
const CulturePartnershipFormLazy = dynamic(() =>
  import("@/app/_components/HubspotForms/CulturePartnershipForm/CulturePartnershipForm").then(mod => ({
    default: mod.CulturePartnershipForm,
  })),
);

export const FormButtonContainer = ({
  children,
  align,
}: {
  children: React.ReactNode;
  align: "center" | "left" | "right" | null | undefined;
}) => {
  return (
    <div
      className={clsx("tw-flex tw-mt-8", {
        "tw-justify-center": align === "center",
        "tw-justify-end": align === "right",
      })}
    >
      {children}
    </div>
  );
};

function TalentInlineForm() {
  const t = useTranslations("FormsClientKeys.talent_representation_form.inline_success");

  return (
    <FormInlineWrapper
      renderForm={({ onSuccess }) => (
        <TalentRepresentationFormLazy onSubmit={submitTalentRepresentation} onSuccess={onSuccess} />
      )}
      successContent={({ onReset }) => (
        <InlineSuccessScreen heading={t("heading")} submitAnotherLabel={t("submit_another")} onReset={onReset}>
          <SuccessStepList>
            {(["review", "contact", "confidential"] as const).map(step => (
              <SuccessStep key={step}>{t(`steps.${step}`)}</SuccessStep>
            ))}
          </SuccessStepList>
        </InlineSuccessScreen>
      )}
    />
  );
}

function CultureInlineForm() {
  const t = useTranslations("FormsClientKeys.culture_partnership_form.inline_success");

  return (
    <FormInlineWrapper
      renderForm={({ onSuccess }) => (
        <CulturePartnershipFormLazy onSubmit={submitCulturePartnership} onSuccess={onSuccess} />
      )}
      successContent={({ onReset }) => (
        <InlineSuccessScreen heading={t("heading")} submitAnotherLabel={t("submit_another")} onReset={onReset}>
          <p className='tw-type-sm tw-text-gray-500 tw-mb-4'>{t("subheading")}</p>
          <SuccessStepList>
            {(["confirmation", "review", "onboarding"] as const).map(step => (
              <SuccessStep key={step}>{t(`steps.${step}`)}</SuccessStep>
            ))}
          </SuccessStepList>
        </InlineSuccessScreen>
      )}
    />
  );
}

export const BlockFormClient = (props: FormBlock) => {
  const { formConfiguration } = props;
  const isButton = formConfiguration.displayMode === "button";

  switch (formConfiguration.template) {
    case "vc":
      if (isButton) {
        return (
          <FormButtonContainer align={formConfiguration.buttonAlign}>
            <VCFormDialogButton
              buttonLabel={formConfiguration.buttonLabel}
              buttonVariant={formConfiguration.buttonVariant ?? undefined}
              buttonSize={formConfiguration.buttonSize ?? undefined}
            />
          </FormButtonContainer>
        );
      }
      return <VCFormLazy onSubmit={submitVCApplication} />;
    case "talent":
      if (isButton) {
        return (
          <FormButtonContainer align={formConfiguration.buttonAlign}>
            <TalentRepresentationFormDialogButton
              buttonLabel={formConfiguration.buttonLabel}
              buttonVariant={formConfiguration.buttonVariant ?? undefined}
              buttonSize={formConfiguration.buttonSize ?? undefined}
            />
          </FormButtonContainer>
        );
      }
      return <TalentInlineForm />;
    case "culture-partnership":
      if (isButton) {
        return (
          <FormButtonContainer align={formConfiguration.buttonAlign}>
            <CulturePartnershipFormDialogButton
              buttonLabel={formConfiguration.buttonLabel}
              buttonVariant={formConfiguration.buttonVariant ?? undefined}
              buttonSize={formConfiguration.buttonSize ?? undefined}
            />
          </FormButtonContainer>
        );
      }
      return <CultureInlineForm />;
    default:
      return null;
  }
};
